/**
 * UI-7.2 — reading a swipe across a picture.
 *
 * A gallery on a phone is moved by pushing the picture aside, not by hitting an
 * arrow the thumb is already covering. Two things decide whether a finger meant
 * that: how far it travelled across the picture, and whether it was crossing it
 * at all or scrolling the page it sits on.
 *
 * Kept out of the components because both the owner's carousel and the
 * visitor's lightbox answer to the same gesture, and because "which way is
 * next" is the kind of arithmetic that is wrong in exactly one language until
 * something says otherwise.
 */

/** Below this the finger was tapping the picture, not paging it. */
export const SWIPE_MIN_TRAVEL = 40;

/**
 * `1` for the next picture, `-1` for the one before, `0` for anything that was
 * not a swipe across the frame. `travelX`/`travelY` are where the finger ended
 * minus where it started, in pixels.
 */
export function swipeStep(travelX: number, travelY: number, rtl: boolean): -1 | 0 | 1 {
  if (Math.abs(travelX) < SWIPE_MIN_TRAVEL) return 0;
  // A thumb on its way down the screen wanders sideways; the page is scrolling,
  // and the gallery under it should stay where it is.
  if (Math.abs(travelX) <= Math.abs(travelY)) return 0;

  // The next picture waits on the side the strip runs towards — left in a
  // left-to-right gallery, right in a right-to-left one — so the finger drags
  // the other way to pull it in.
  const forward = rtl ? travelX > 0 : travelX < 0;
  return forward ? 1 : -1;
}
